/**
 * Pure helpers for the Edit role form — kept out of the component like `proposeRole.ts`.
 */
import type { RoleEditValues, RoleSource } from '../../services/plannedRoleEditsApi';

/**
 * Only the fields that differ from what the record holds.
 *
 * The proxy drops unchanged values itself, but sending a diff keeps the audit log honest about
 * what the person actually touched, and means a row with a vocabulary value the form cannot show
 * (a legacy choice, `CAC`) is never re-sent — and so never refused — just because it was on screen.
 */
export function changedValues(original: RoleEditValues, form: RoleEditValues): Partial<RoleEditValues> {
  const out: Partial<RoleEditValues> = {};
  (Object.keys(form) as (keyof RoleEditValues)[]).forEach((key) => {
    const next = (form[key] ?? '').trim();
    if (next !== (original[key] ?? '').trim()) out[key] = next;
  });
  return out;
}

/**
 * A vocabulary with the record's CURRENT value kept in it.
 *
 * The form's choices are the live Airtable ones minus the hidden ones (`CAC`), and a hiring-plan
 * row can carry a value that list does not offer. Without it the `<select>` would silently show
 * the first option — so saving an unrelated change would appear to have changed this one too.
 */
export function withCurrent(options: string[], current: string): string[] {
  if (!current || options.includes(current)) return options;
  return [current, ...options];
}

/**
 * The date the role is costed on while the form is open — the same Actual > Adjusted > Planned
 * rule the proxy uses, minus Actual (an editable row has not started). On a hiring-plan row an
 * empty adjusted date falls back to the committed planned one.
 */
export function effectiveFormStart(source: RoleSource, startDate: string, plannedStartDate: string | null): string {
  if (startDate) return startDate;
  return source === 'hiring_plan' ? plannedStartDate ?? '' : '';
}
